/* eslint-disable waimaie/no-direct-access-localstorage */

/**
 * 小票模板加载服务
 *
 * 首次从服务端加载全部门店的模板信息，其后刷新页面请求时会带上本地缓存数据，服务端只返回增量数据
 */

import { db } from '@utils/wmIndexDB';
import request from '@utils/axiosWrapper';
import { reportCustomMetrics, CUSTOM_METRICS } from '@utils/owlReporter';
import * as Ids from '@utils/ids';

//存储小票模板内容
window.PRINTTEMPLATES = {};
//存储标签模板内容
window.TAGPRINTTEMPLATES = {};

/**
 * 获取本地存储的小票模板缓存，id 和 url 对应关系（与门店无关）
 * ```json
 * {"358":"https://a.com/tpl123.xml", "359":"https://a.com/tpl123.xml"}
 * ```
 */
const printTemplatesStr = localStorage.getItem('printTemplates');

/**
 * 获取本地存储的标签模板缓存，id 和 url 对应关系（与门店无关）
 * ```json
 * {"358":"https://a.com/tpl123.xml", "359":"https://a.com/tpl123.xml"}
 * ```
 */
const tagPrintTemplatesStr = localStorage.getItem('tagPrintTemplates');

/**
 * 获取本地存储的门店和小票模板 id 对应关系，形如
 * ```json
 * [
 *  {wmPoiId: 8353664, version: 1, nowTemplateId: 358},
 *  {wmPoiId: 8353664, version: 1, nowTemplateId: 359}
 * ]
 * ```
 */
const poiPrintTemplatesStr = localStorage.getItem('poiPrintTemplates');

/**
 * 获取本地存储的门店和标签模板 id 对应关系，形如
 * ```json
 * [
 *  {wmPoiId: 8353664, version: 1, nowTemplateId: 358},
 *  {wmPoiId: 8353664, version: 1, nowTemplateId: 359}
 * ]
 * ```
 */
const tagPoiPrintTemplatesStr = localStorage.getItem('tagPoiPrintTemplates');

/** 模板ID与模板下载地址的映射关系 */
type PrintTemplateUrlMap = {
    [index: string]: string;
};

/** 门店模板配置，主要包括门店ID与模板ID */
type PoiTemplateConfig = {
    wmPoiId: number;
    version: number;
    nowTemplateId: number;
};

/** 模板使用情况 */
type TemplateUsageInfo = {
    /** 模板ID */
    nowTemplateId: number;
    templateMd5: string;
    /** 模板下载地址 */
    templateUrl: string;
    /** 使用该模板的门店ID列表 */
    wmPoiIdList: number[];
};

const printTemplates: PrintTemplateUrlMap = printTemplatesStr ? JSON.parse(printTemplatesStr) : {};
const tagPrintTemplates: PrintTemplateUrlMap = tagPrintTemplatesStr
    ? JSON.parse(tagPrintTemplatesStr)
    : {};
const poiPrintTemplates: PoiTemplateConfig[] = poiPrintTemplatesStr
    ? JSON.parse(poiPrintTemplatesStr)
    : [];
const tagPoiPrintTemplates: PoiTemplateConfig[] = tagPoiPrintTemplatesStr
    ? JSON.parse(tagPoiPrintTemplatesStr)
    : [];

/**
 * 前端小票模板容灾
 *
 * 同一个templateId对应的模板内容，一旦发布就不会再变化。
 * 端上一旦加载成功就可以缓存在设备上避免网络抖动导致的小票加载失败导致的打印失败。
 */
const TEMPLATE_CONTENT_CACHE_KEY = 'printTemplatesContentCache';
let templateContentCache: { [key: number]: string } = {};
try {
    templateContentCache = JSON.parse(localStorage.getItem(TEMPLATE_CONTENT_CACHE_KEY) || '{}');
    window.PRINTTEMPLATES = { ...templateContentCache }; // 提前将本地缓存数据加载到内存中
} catch (e) {
    localStorage.removeItem(TEMPLATE_CONTENT_CACHE_KEY);
}

/**
 * 读取当前本地缓存的某个门店使用的小票模板ID
 *
 * @param wmPoiId 门店ID
 * @param type 模板类型
 * @returns 当前本地缓存的模板ID，若本地没有缓存则返回0
 */
const getPoiNowTemplateId = (wmPoiId: number, type: 'receipt' | 'tag'): number => {
    let findList = poiPrintTemplates;
    if (type === 'tag') {
        findList = tagPoiPrintTemplates;
    }

    for (let i = 0; i < findList.length; i++) {
        if (findList[i].wmPoiId === wmPoiId) {
            const nowTemplateId = findList[i].nowTemplateId;

            if (type === 'receipt') {
                // 小票模板额外增加判断，只有当本地存在该模板的内容或是存在该模板的加载地址时，才可以通知打印云存在缓存
                // 否则，如果模板内容与模板路径信息本地均丢失，且门店的模板ID在服务端没有更新的情况下，服务端不会下发有效信息，导致模板丢失
                if (templateContentCache[nowTemplateId] || printTemplates[nowTemplateId]) {
                    return nowTemplateId;
                } else {
                    return 0;
                }
            } else {
                return nowTemplateId;
            }
        }
    }

    return 0;
};

// waimai_e域名的容灾模板
const defaultTemplatePromise = request.rawGet<string>('/static/docs/defaultPrintTemplate.xml', {
    responseType: 'text',
});

const initTempalteData = () => {
    db.poiList.toArray().then(allPoiList => {
        if (allPoiList.length > 0) {
            // 根据本地缓存数据构造请求打印云接口的参数
            const nowPoiPrintTemplates: PoiTemplateConfig[] = [];
            const nowTagPoiPrintTemplates: PoiTemplateConfig[] = [];

            allPoiList.forEach(poi => {
                if (poi.id !== -1) {
                    nowPoiPrintTemplates.push({
                        version: 1,
                        wmPoiId: poi.id,
                        nowTemplateId: getPoiNowTemplateId(poi.id, 'receipt'),
                    });

                    if (poi.groupMealPoi === 1) {
                        nowTagPoiPrintTemplates.push({
                            version: 1,
                            wmPoiId: poi.id,
                            nowTemplateId: getPoiNowTemplateId(poi.id, 'tag'),
                        });
                    }
                }
            });

            // 使用构造好的缓存数据，请求打印云
            // 打印云只会返回和本地缓存差异化的数据，如果本地缓存的信息均有效，则返回两个空数组
            request
                .rawPostAsFormSubmit<{
                    code: number;
                    data: {
                        wmPrintTemplate: TemplateUsageInfo[];
                        groupLabelPrintTemplate: TemplateUsageInfo[];
                    };
                }>('/v2/shop/systemSettings/r/getPrintTemplate/new', {
                    nowPrintTemplate: JSON.stringify(nowPoiPrintTemplates),
                    nowLabelPrintTemplate: JSON.stringify(nowTagPoiPrintTemplates),
                })
                .then(
                    response => {
                        reportCustomMetrics(CUSTOM_METRICS.PRINT_TEMPLATE_API.KEY, 1, {
                            [CUSTOM_METRICS.PRINT_TEMPLATE_API.TAGS.HTTP_SUCCESS]: 1,
                            [CUSTOM_METRICS.PRINT_TEMPLATE_API.TAGS.BUSINESS_CODE]: response.code,
                            [CUSTOM_METRICS.PRINT_TEMPLATE_API.TAGS.ACCT_ID]: Ids.getAcctId(),
                            [CUSTOM_METRICS.PRINT_TEMPLATE_API.TAGS.POI_COUNT]:
                                nowPoiPrintTemplates.length + nowTagPoiPrintTemplates.length,
                        });
                        // 此处判断 response.data 非空是为了屏蔽食亨，正常业务场景不会返回空
                        if (response.code === 0 && response.data) {
                            const { wmPrintTemplate, groupLabelPrintTemplate } = response.data;

                            // 更新小票模板配置
                            wmPrintTemplate.forEach(template => {
                                printTemplates[template.nowTemplateId] = template.templateUrl;

                                nowPoiPrintTemplates.forEach(poiTempalteConfig => {
                                    if (template.wmPoiIdList.includes(poiTempalteConfig.wmPoiId)) {
                                        poiTempalteConfig.nowTemplateId = template.nowTemplateId;
                                    }
                                });
                            });

                            // 更新标签模板配置
                            groupLabelPrintTemplate.forEach(template => {
                                tagPrintTemplates[template.nowTemplateId] = template.templateUrl;

                                nowTagPoiPrintTemplates.forEach(poiTempalteConfig => {
                                    if (template.wmPoiIdList.includes(poiTempalteConfig.wmPoiId)) {
                                        poiTempalteConfig.nowTemplateId = template.nowTemplateId;
                                    }
                                });
                            });

                            //遍历模板，如果没有被任何poi在使用，则删除；否则请求模板内容，存储到全局变量中
                            const templateLoadingPromiseList = Object.keys(printTemplates).map(
                                templateId => {
                                    if (
                                        nowPoiPrintTemplates.some(
                                            poiTempalteConfig =>
                                                poiTempalteConfig.nowTemplateId ===
                                                Number(templateId),
                                        )
                                    ) {
                                        // 还在使用，如果本地没有缓存则请求模板内容
                                        if (!window.PRINTTEMPLATES[templateId]) {
                                            const requestUrl = printTemplates[templateId]
                                                .replace('https:', '')
                                                .replace('http:', '');

                                            return request
                                                .rawGet(requestUrl, {
                                                    responseType: 'text',
                                                })
                                                .then((tempalteContent: string) => {
                                                    window.PRINTTEMPLATES[
                                                        templateId
                                                    ] = tempalteContent;
                                                    templateContentCache[
                                                        Number(templateId)
                                                    ] = tempalteContent;
                                                })
                                                .catch(() => {
                                                    // 小票模板加载失败，使用兜底模板，兜底模板不要写进localStorage缓存
                                                    defaultTemplatePromise.then(defaultTemplate => {
                                                        window.PRINTTEMPLATES[
                                                            templateId
                                                        ] = defaultTemplate;
                                                    });
                                                });
                                        } else {
                                            return Promise.resolve();
                                        }
                                    } else {
                                        // 不在使用，从缓存中删除
                                        delete printTemplates[templateId];
                                        delete templateContentCache[Number(templateId)];
                                        return Promise.resolve();
                                    }
                                },
                            );

                            Promise.all(templateLoadingPromiseList).then(() => {
                                // 全部模板完成加载（不管成功与否），此时保留templateContentCache中Id最大的三个模板(防止写爆localStorage)，然后写回localStorage
                                const loadedTemplateIds = Object.keys(templateContentCache);
                                loadedTemplateIds.sort((id1, id2) => Number(id2) - Number(id1));
                                const rebuiltTemplateContentCache: { [key: number]: string } = {};
                                loadedTemplateIds.slice(0, 3).forEach(tempalteId => {
                                    rebuiltTemplateContentCache[Number(tempalteId)] =
                                        templateContentCache[Number(tempalteId)];
                                });
                                localStorage.setItem(
                                    TEMPLATE_CONTENT_CACHE_KEY,
                                    JSON.stringify(rebuiltTemplateContentCache),
                                );
                            });

                            // 循环判断、加载标签模板
                            Object.keys(tagPrintTemplates).forEach(templateId => {
                                if (
                                    nowTagPoiPrintTemplates.some(
                                        poiTempalteConfig =>
                                            poiTempalteConfig.nowTemplateId === Number(templateId),
                                    )
                                ) {
                                    // 还在使用，请求模板内容
                                    const requestUrl = tagPrintTemplates[templateId]
                                        .replace('https:', '')
                                        .replace('http:', '');

                                    request
                                        .rawGet(requestUrl, {
                                            responseType: 'text',
                                        })
                                        .then((tempalteContent: string) => {
                                            window.TAGPRINTTEMPLATES[templateId] = tempalteContent;
                                        });
                                } else {
                                    // 不在使用，从缓存中删除
                                    delete tagPrintTemplates[templateId];
                                }
                            });

                            //回存到本地
                            localStorage.setItem('printTemplates', JSON.stringify(printTemplates));
                            localStorage.setItem(
                                'poiPrintTemplates',
                                JSON.stringify(nowPoiPrintTemplates),
                            );
                            localStorage.setItem(
                                'tagPrintTemplates',
                                JSON.stringify(tagPrintTemplates),
                            );
                            localStorage.setItem(
                                'tagPoiPrintTemplates',
                                JSON.stringify(nowTagPoiPrintTemplates),
                            );
                        }
                    },
                    () => {
                        reportCustomMetrics(CUSTOM_METRICS.PRINT_TEMPLATE_API.KEY, 1, {
                            [CUSTOM_METRICS.PRINT_TEMPLATE_API.TAGS.HTTP_SUCCESS]: 0,
                            [CUSTOM_METRICS.PRINT_TEMPLATE_API.TAGS.ACCT_ID]: Ids.getAcctId(),
                            [CUSTOM_METRICS.PRINT_TEMPLATE_API.TAGS.POI_COUNT]:
                                nowPoiPrintTemplates.length + nowTagPoiPrintTemplates.length,
                        });
                    },
                );
        } else {
            //清除本地数据
            localStorage.removeItem('printTemplates');
            localStorage.removeItem('poiPrintTemplates');
            localStorage.removeItem('tagPrintTemplates');
            localStorage.removeItem('tagPoiPrintTemplates');
        }
    });
};

export default { initTempalteData };
